import React, { useEffect, useMemo } from "react"
import { useSelector, useDispatch } from "react-redux"
import { setPlotType } from "../logic/chartPlot"
import { setSymbolAndLegs } from "../logic/chartSymbolAndLeg"
import _ from "lodash"
import useCprovder from "../logic/useCprovider"
import useChartResize from "../logic/useChartResize"
import chartColors from "../logic/chartColor"
import {
  RENDER_COUNT,
  LIMIT_RENDER_COUNT_MINI,
  RENDER_COUNT_EXTERNAL,
} from "../logic/chartConfig"
import { setSubChart } from "../logic/chartSub"
import { setThemeFunction } from "utils/util"
import { UserStorage } from "utils/userStorage"
import { useEffectFirstSkip } from "components/actions/customHook/useEffectFirstSkip"
import { setAddonChart, removeAddonChart } from "../logic/chartAddon"
import { setKVOnOff } from "components/atoms/chart/logic/chartTools"
import { resultObj } from "../logic/chartProvider"
import { LoadStateType } from "../../../../redux/ducks/PageLoad"
import store from "redux/store"

interface Window {
  Logic: any
  IntelliFactory: any
  Trading: any
}

declare var window: Window

interface props {
  canvasId: string
  miniChartFlag?: boolean
  priorityFlag?: boolean
  priorityHeight?: Array<number>
  linkTechChart?: boolean
}

const MiniChart = React.memo((props: props) => {
  const {
    canvasId,
    miniChartFlag,
    priorityFlag,
    priorityHeight,
    linkTechChart,
  } = props
  // Selector
  const plotTypeSelector = (state: any) => state.chart[canvasId].plotType
  const providersSelector = (state: any) => state.chart[canvasId].providers
  const chartSettingSelector = (state: any) => state.chart.chartSetting
  const symbolSelector = (state: any) => state.chart[canvasId].symbol
  const subChartSelector = (state: any) => state.chart[canvasId].subChart
  const termSelector = (state: any) => state.chart[canvasId].term
  const colorSettingSelector = (state: any) => state.chart.colorSetting
  const renderCountSelector = (state: any) => state.chart[canvasId].renderCount

  const plotType = useSelector(plotTypeSelector)
  const term = useSelector(termSelector)
  const providers = useSelector(providersSelector)
  const [_provider, _controller, _procs] = providers
  const subChart = useSelector(subChartSelector)
  const symbol = useSelector(symbolSelector)
  const colorSetting = useSelector(colorSettingSelector)
  const chartSetting = useSelector(chartSettingSelector)
  const renderCount = useSelector(renderCountSelector)

  const [createCProvider, cursor] = useCprovder({
    canvasId: canvasId,
    miniChartFlag: miniChartFlag,
  })
  const isPageLoading = useSelector(
    (state: { PageLoad: LoadStateType }) => state.PageLoad.load
  )
  const { resize, removeResize, size } = useChartResize({
    canvasId: canvasId,
    parentElement: `chart-body-${canvasId}`,
    minichartFlag: miniChartFlag,
    effector: priorityHeight && !!priorityHeight[0],
  })
  const { width, height, scaleWidth, scaleHeight } = size
  const _setKVOnOff = useMemo(() => {
    return setKVOnOff(_controller)
  }, [_controller])

  const applyOption = () => {
    setPlotType(plotType.key, _controller)
    chartColors.setColorPattern(plotType.key, colorSetting, _controller)
    setSymbolAndLegs(
      term.key,
      symbol,
      _provider,
      _controller,
      miniChartFlag
        ? LIMIT_RENDER_COUNT_MINI
        : priorityFlag
        ? RENDER_COUNT_EXTERNAL[term.id]
        : RENDER_COUNT[term.id]
    )
    _controller.Chart.set_BarNumMargin([renderCount * 0.5, 5])
    if (props.miniChartFlag) {
      _controller.Chart.set_Setting(
        window.IntelliFactory.Runtime.New(
          window.Trading.Chart.BaseTypes.ChartSetting,
          {
            EnableScrollBar: false,
            EnableSplitterMH: false,
            EnableMovePortMaxMH: false,
            EnableMoveYMinMaxPMH: false,
          }
        )
      )
      // ミニチャートは、基本的に銘柄が変更されないため、ここで実行。
      // ミドルチャートの場合、銘柄変更のタイミングが悪いとエラーを起こすため、useEffect内で実行する。
      _setAddonChart()
    }
  }

  const _setAddonChart = () => {
    setAddonChart(chartSetting, "AverageN", _controller, term.key, [
      "AverageN_1",
      "AverageN_2",
      "AverageN_3",
    ])
    if (!_controller) return
    _setKVOnOff("useParamList", false)
  }

  useEffect(() => {
    if (window.Logic === undefined) return
    createCProvider(priorityFlag)
  }, [])

  useEffect(() => {
    if (!_controller) return
    applyOption()
    resize()
    return () => removeResize()
  }, [_controller])

  //ヘッダー更新ボタン
  useEffect(() => {
    isPageLoading && applyOption()
  }, [isPageLoading])

  useEffectFirstSkip(() => {
    if (!_controller) return
    setSymbolAndLegs(
      term.key,
      symbol,
      _provider,
      _controller,
      miniChartFlag
        ? LIMIT_RENDER_COUNT_MINI
        : priorityFlag
        ? RENDER_COUNT_EXTERNAL[term.id]
        : RENDER_COUNT[term.id]
    )
  }, [term])

  useEffectFirstSkip(() => {
    if (!_controller) return
    setSymbolAndLegs(
      term.key,
      symbol,
      _provider,
      _controller,
      miniChartFlag
        ? LIMIT_RENDER_COUNT_MINI
        : priorityFlag
        ? RENDER_COUNT_EXTERNAL[term.id]
        : RENDER_COUNT[term.id]
    )
  }, [symbol])

  useEffectFirstSkip(() => {
    _setAddonChart()
  }, [symbol, term])

  useEffect(() => {
    if (!_controller) return
    setPlotType(plotType.key, _controller)
  }, [plotType])

  useEffect(() => {
    if (!_controller) return
    UserStorage.ChartColor.set(colorSetting)
    chartColors.setColorPattern(plotType.key, colorSetting, _controller)
  }, [colorSetting])

  // useEffect(() => {
  //   console.log(subChart)
  //   if (!_controller || _.isEmpty(subChart)) return
  //   console.log("into")
  //   setSubChart(chartSetting, subChart[0].key, _controller)
  // }, [subChart])

  return (
    <div
      id={`chart-body-${canvasId}`}
      style={{ width: "100%", height: "100%" }}
    >
      <canvas
        id={canvasId}
        width={scaleWidth}
        height={priorityHeight ? priorityHeight[0] : scaleHeight}
        style={{
          cursor: !miniChartFlag ? cursor : !linkTechChart ? cursor : "pointer", //押下でテクニカルチャートへ遷移するミニチャートのみ"pointer"
          width: `${width}px`,
          height: `${priorityHeight ? priorityHeight[1] : height}px`,
        }}
      />
    </div>
  )
})

export default MiniChart
